// src/pages/bridges/BridgePage.tsx
import {
  Activity,
  Filter,
  Layers,
  Network,
  Plus,
  RefreshCw,
  Search,
  Server,
  Trash2,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchApp } from "../../services/apiService";

import BridgeFormModal, {
  Bridge,
  BridgeFormMode,
  BridgeFormState,
} from "../../components/bridge/BridgeFormModal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";

interface RouterBridges {
  router_id: number;
  router_name: string;
  router_ip: string;
  bridges: (Bridge & {
    arp?: string;
    mtu?: number | string;
    l2mtu?: number | null;
    mac_address?: string | null;
    ageing_time?: string | null;
    igmp_snooping?: boolean;
    dhcp_snooping?: boolean;
    vlan_filtering?: boolean;
    fast_forward?: boolean;
    running?: boolean;
    rx_bytes?: number | null;
    tx_bytes?: number | null;
    rx_packets?: number | null;
    tx_packets?: number | null;
    comment?: string | null;
  })[];
}

// Flattened type for easier table rendering
interface FlattenedBridge {
  routerId: number;
  routerName: string;
  routerIp: string;
  data: RouterBridges["bridges"][0];
}

const BridgePage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Data State
  const [routerBridges, setRouterBridges] = useState<RouterBridges[]>([]);
  const [loadingBridges, setLoadingBridges] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [selectedRouterId, setSelectedRouterId] = useState<string>("all");

  // Form State
  const [bridgeForm, setBridgeForm] = useState<BridgeFormState>({
    name: "",
    comment: "",
    arp: "enabled",
    ageingTime: "5m",
    mtu: "1500",
    igmpSnooping: "no",
    dhcpSnooping: "no",
    vlanFiltering: "no",
    fastForward: "no",
  });
  const [bridgeFormLoading, setBridgeFormLoading] = useState(false);

  // UI State
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<BridgeFormMode>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Confirm Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    routerId: number;
    name: string;
  } | null>(null);

  // ---------- Helpers ----------

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const showError = (msg: string) => {
    setError(msg);
    setSuccess(null);
  };

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setError(null);
  };

  const formatBytes = (bytes?: number | null) => {
    if (!bytes || bytes <= 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${parseFloat(value.toFixed(2))} ${sizes[i]}`;
  };

  const openModal = () => {
    resetMessages();
    setModalMode("bridge");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalMode(null);
  };

  // ---- API calls ----
  const fetchBridges = async () => {
    try {
      setLoadingBridges(true);
      const data = await fetchApp<RouterBridges[]>("/bridge/");
      setRouterBridges(Array.isArray(data) ? data : []);
    } catch (err: any) {
      showError(err.message || "Failed to load bridges");
      showToast(err.message || "Failed to load bridges", "error");
    } finally {
      setLoadingBridges(false);
    }
  };

  useEffect(() => {
    fetchBridges();
  }, []);

  // ---- Data Flattening & Filtering ----

  const allBridges = useMemo<FlattenedBridge[]>(() => {
    return routerBridges.flatMap((router) =>
      router.bridges.map((bridge) => ({
        routerId: router.router_id,
        routerName: router.router_name,
        routerIp: router.router_ip,
        data: bridge,
      })),
    );
  }, [routerBridges]);

  const filteredBridges = useMemo(() => {
    return allBridges.filter((item) => {
      // Router Filter
      if (
        selectedRouterId !== "all" &&
        item.routerId.toString() !== selectedRouterId
      ) {
        return false;
      }

      // Search Filter
      const term = search.toLowerCase().trim();
      if (!term) return true;

      return (
        item.data.name.toLowerCase().includes(term) ||
        item.routerName.toLowerCase().includes(term) ||
        (item.data.comment && item.data.comment.toLowerCase().includes(term)) ||
        (item.data.mac_address &&
          item.data.mac_address.toLowerCase().includes(term))
      );
    });
  }, [allBridges, search, selectedRouterId]);

  // Stats
  const stats = useMemo(() => {
    const total = filteredBridges.length;
    let running = 0;
    filteredBridges.forEach((b) => {
      if (b.data.running) running++;
    });
    return { total, running };
  }, [filteredBridges]);

  // ---- Handlers ----
  const handleCreateBridge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bridgeForm.name.trim()) {
      showToast("Bridge name is required", "error");
      showError("Bridge name is required");
      return;
    }

    // Default to first router if none selected (logic can be improved to ask user)
    const targetRouterId = routerBridges[0]?.router_id;
    if (!targetRouterId) {
      const msg = "No router available for creating a bridge";
      showToast(msg, "error");
      showError(msg);
      return;
    }

    try {
      setBridgeFormLoading(true);
      resetMessages();

      const body = {
        name: bridgeForm.name.trim(),
        comment: bridgeForm.comment.trim() || undefined,
        arp: bridgeForm.arp,
        ageing_time: bridgeForm.ageingTime,
        mtu: bridgeForm.mtu,
        igmp_snooping: bridgeForm.igmpSnooping,
        dhcp_snooping: bridgeForm.dhcpSnooping,
        vlan_filtering: bridgeForm.vlanFiltering,
        fast_forward: bridgeForm.fastForward,
      };

      const res = await fetchApp<any>(`/bridge?router_id=${targetRouterId}`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (res && typeof res === "object" && "error" in res) {
        throw new Error(res.error || "Failed to create bridge");
      }

      showToast("Bridge created successfully!", "success");
      showSuccess("Bridge created successfully");

      setBridgeForm({
        name: "",
        comment: "",
        arp: "enabled",
        ageingTime: "5m",
        mtu: "1500",
        igmpSnooping: "no",
        dhcpSnooping: "no",
        vlanFiltering: "no",
        fastForward: "no",
      });
      closeModal();
      await fetchBridges();
    } catch (err: any) {
      const msg = err?.message || "Failed to create bridge";
      showToast(msg, "error");
      showError(msg);
    } finally {
      setBridgeFormLoading(false);
    }
  };

  const askDeleteBridge = (routerId: number, name: string) => {
    setPendingDelete({ routerId, name });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;

    try {
      setConfirmLoading(true);
      resetMessages();

      const res = await fetchApp<any>(
        `/bridge/${encodeURIComponent(pendingDelete.name)}?router_id=${
          pendingDelete.routerId
        }`,
        {
          method: "DELETE",
        },
      );

      if (res && typeof res === "object" && "error" in res) {
        throw new Error(res.error || "Failed to delete bridge");
      }

      showToast("Bridge deleted successfully!", "success");
      showSuccess("Bridge deleted successfully");
      await fetchBridges();
    } catch (err: any) {
      const msg = err?.message || "Failed to delete bridge";
      showToast(msg, "error");
      showError(msg);
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  // ---- Render Helpers ----

  const renderStatusBadge = (running?: boolean) => {
    if (running) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-100">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Running
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 border border-slate-200">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
        Idle
      </span>
    );
  };

  return (
    <div className="flex-1 bg-slate-50/50 px-4 py-6 lg:px-8 min-h-screen">
      <div className="mx-auto max-w-[1600px] space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              Bridges
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage Layer 2 bridges and port configurations across your
              network.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700">
                  {stats.total}
                </span>
                <span className="text-xs text-slate-500">Total Bridges</span>
              </div>
              <div className="h-4 w-px bg-slate-200" />
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-semibold text-slate-700">
                  {stats.running}
                </span>
                <span className="text-xs text-slate-500">Running</span>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Left: Filters */}
            <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
              {/* Search */}
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search bridges..."
                  className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-4 text-sm text-slate-900 placeholder:text-slate-500 focus:border-slate-900 focus:outline-none focus:ring-0 transition-colors"
                />
              </div>

              {/* Router Filter */}
              <div className="relative w-full md:w-64">
                <Filter className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <select
                  value={selectedRouterId}
                  onChange={(e) => setSelectedRouterId(e.target.value)}
                  className="h-10 w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-8 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-0 transition-colors cursor-pointer"
                >
                  <option value="all">All Routers</option>
                  {routerBridges.map((router) => (
                    <option key={router.router_id} value={router.router_id}>
                      {router.router_name} ({router.router_ip})
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                  <svg
                    className="h-4 w-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  resetMessages();
                  fetchBridges();
                }}
                disabled={loadingBridges}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loadingBridges ? "animate-spin" : ""}`}
                />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              <button
                onClick={openModal}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800 shadow-sm"
              >
                <Plus className="h-4 w-4" />
                <span>New Bridge</span>
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {(error || success) && (
          <div
            className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3 text-sm shadow-sm animate-in slide-in-from-top-2 ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-800"
                : "border-emerald-200 bg-emerald-50 text-emerald-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <div
                className={`h-2 w-2 rounded-full ${
                  error ? "bg-rose-500" : "bg-emerald-500"
                }`}
              />
              <p className="font-medium">{error || success}</p>
            </div>
            <button
              onClick={resetMessages}
              className="rounded-full p-1 opacity-60 hover:bg-black/5 hover:opacity-100 transition-all"
            >
              <span className="sr-only">Dismiss</span>
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Main Table */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="px-4 py-3 font-semibold text-slate-900">
                    Bridge Name
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-900">
                    Router
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-900">
                    Status
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-900">
                    ARP
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-900">
                    MTU / L2
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-900">
                    MAC Address
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-900 text-right">
                    Traffic (RX/TX)
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-900 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loadingBridges ? (
                  // Skeleton
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-4">
                        <div className="h-4 w-32 rounded bg-slate-100"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-24 rounded bg-slate-100"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-5 w-16 rounded-full bg-slate-100"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-12 rounded bg-slate-100"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-20 rounded bg-slate-100"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 w-28 rounded bg-slate-100"></div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="ml-auto h-4 w-24 rounded bg-slate-100"></div>
                      </td>
                      <td className="px-4 py-4"></td>
                    </tr>
                  ))
                ) : filteredBridges.length > 0 ? (
                  filteredBridges.map((item, idx) => (
                    <tr
                      key={`${item.routerId}-${item.data.name}-${idx}`}
                      className="group cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() =>
                        navigate(
                          `/bridges/${encodeURIComponent(item.data.name)}`,
                          { state: { routerId: item.routerId } },
                        )
                      }
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2 font-medium text-slate-900 group-hover:text-indigo-600 transition-colors">
                          <Network className="h-3.5 w-3.5 text-slate-400" />
                          {item.data.name}
                        </div>
                        {item.data.comment && (
                          <div
                            className="ml-5.5 text-xs text-slate-500 mt-0.5 truncate max-w-[200px]"
                            title={item.data.comment}
                          >
                            {item.data.comment}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Server className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs font-medium">
                            {item.routerName}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400 pl-5.5">
                          {item.routerIp}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {renderStatusBadge(item.data.running)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="text-xs text-slate-600">
                          {item.data.arp || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-xs text-slate-600">
                          <span className="font-medium text-slate-700">
                            {item.data.mtu || "—"}
                          </span>
                          <span className="text-slate-400 mx-1">/</span>
                          {item.data.l2mtu || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="font-mono text-xs text-slate-500">
                          {item.data.mac_address || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="text-xs text-emerald-600 font-medium">
                            {formatBytes(item.data.rx_bytes)} ↓
                          </span>
                          <span className="text-xs text-indigo-600 font-medium">
                            {formatBytes(item.data.tx_bytes)} ↑
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            askDeleteBridge(item.routerId, item.data.name);
                          }}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Delete Bridge"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-16 text-center">
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                        <Layers className="h-6 w-6 text-slate-400" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900">
                        No bridges found
                      </h3>
                      <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
                        We couldn't find any bridges matching your filters. Try
                        adjusting your search or create a new bridge.
                      </p>
                      <button
                        onClick={() => {
                          setSearch("");
                          setSelectedRouterId("all");
                        }}
                        className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                      >
                        Clear all filters
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-xs text-slate-500 flex justify-between items-center">
            <span>Showing {filteredBridges.length} bridges</span>
          </div>
        </div>
      </div>

      {/* Modal for creating bridge */}
      <BridgeFormModal
        isOpen={isModalOpen}
        mode={modalMode}
        onClose={closeModal}
        bridgeForm={bridgeForm}
        setBridgeForm={setBridgeForm}
        bridgeFormLoading={bridgeFormLoading}
        handleCreateBridge={handleCreateBridge}
      />

      {/* Confirm delete dialog */}
      <ConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        title="Delete Bridge"
        description={
          pendingDelete
            ? `Are you sure you want to delete the bridge "${pendingDelete.name}"? This action cannot be undone and may disrupt network connectivity for ports assigned to it.`
            : ""
        }
        confirmLabel="Delete Bridge"
        cancelLabel="Cancel"
        variant="danger"
        onCancel={() => {
          if (confirmLoading) return;
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default BridgePage;
